import React from 'react';
import { motion } from 'framer-motion';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Check, Repeat, Zap } from 'lucide-react';

const ConnectedToolCard = ({ label, logo, glow }) => (
  <Box position="relative">
    <Box
      minW={{ base: '112px', md: '132px' }}
      px={{ base: 4, md: 5 }}
      py={{ base: 4, md: 5 }}
      borderRadius="2xl"
      bg="white"
      borderWidth="1px"
      borderColor="landing.border"
      boxShadow={`0 16px 32px ${glow}`}
    >
      <Flex direction="column" alignItems="center" gap={3}>
        <Box
          w={{ base: '44px', md: '52px' }}
          h={{ base: '44px', md: '52px' }}
          borderRadius="xl"
          bg="brand.subtle"
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="sm"
        >
          <img
            src={logo}
            alt={label}
            style={{ width: '28px', height: '28px', objectFit: 'contain', display: 'block' }}
          />
        </Box>
        <Text fontSize={{ base: '10px', md: '11px' }} fontWeight="900" color="text.main" letterSpacing="0.08em">
          {label.toUpperCase()}
        </Text>
      </Flex>
    </Box>
  </Box>
);

const ConnectedToolsVisual = () => (
  <Flex alignItems="center" justifyContent="center" gap={{ base: 3, md: 5 }} p={4} w="100%">
    <ConnectedToolCard
      label="Jira"
      logo="https://cdn.simpleicons.org/jira/0052CC"
      glow="rgba(0, 82, 204, 0.14)"
    />

    <Box position="relative" w={{ base: '72px', md: '110px' }} h="4px" flexShrink={0}>
      <Box
        position="absolute"
        inset="0"
        borderRadius="full"
        bg="rgba(37, 99, 235, 0.12)"
      />
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          borderRadius: '999px',
          background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.15), #10B981, rgba(16, 185, 129, 0.15))',
        }}
        animate={{ width: ['0%', '100%', '0%'] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
      />
      <Flex
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        px={2}
        py={1}
        borderRadius="full"
        bg="white"
        borderWidth="1px"
        borderColor="success.100"
        boxShadow="sm"
        alignItems="center"
        gap={1}
        zIndex={1}
      >
        <Flex
          w="16px"
          h="16px"
          borderRadius="full"
          bg="success.500"
          color="white"
          alignItems="center"
          justifyContent="center"
          boxShadow="0 6px 12px rgba(16, 185, 129, 0.24)"
        >
          <Check size={10} strokeWidth={3} />
        </Flex>
        <Text fontSize="8px" fontWeight="900" color="success.700" letterSpacing="0.06em">
          CONNECTED
        </Text>
      </Flex>
    </Box>

    <ConnectedToolCard
      label="ClickUp"
      logo="https://cdn.simpleicons.org/clickup/7B68EE"
      glow="rgba(123, 104, 238, 0.16)"
    />
  </Flex>
);

const HowItWorks = () => {
  const navigate = useNavigate();

  const steps = [
    {
      number: "01",
      title: "Connect Your Tools",
      description: "Securely authorize your platforms in one click. DatLynk establishes a 60-second connection to your entire toolstack—no coding required.",
      visual: <ConnectedToolsVisual />
    },
    {
      number: "02",
      title: "Map Your Structure",
      description: "Decide how tasks should match. Connect epics, tasks, and subtasks across your tools in a few clicks.",
      visual: (
        <Box
          p={6}
          w="100%"
          maxW="320px"
          borderRadius="xl"
          bg="brand.subtle"
          borderWidth="1px"
          borderColor="landing.border"
          fontFamily="mono"
          fontSize="12px"
          boxShadow="inner"
        >
          {[
            ['Jira Epic', 'ClickUp List'],
            ['Issue Type', 'Task Status'],
            ['Sprint', 'Due Date'],
          ].map(([from, to], i) => (
            <Flex key={i} alignItems="center" justifyContent="space-between" mb={i < 2 ? 3 : 0}>
              <Box px={3} py={1.5} borderRadius="md" bg="white" color="text.main" boxShadow="sm" fontWeight="700">{from}</Box>
              <Box h="2px" flex={1} mx={3} bg="landing.border" position="relative" display="flex" alignItems="center" justifyContent="center">
                 <Box bg="brand.subtle" px={1}>
                    <Box color="brand.primary" display="inline-flex"><Zap size={12} fill="currentColor" /></Box>
                 </Box>
              </Box>
              <Box px={3} py={1.5} borderRadius="md" bg="brand.primary" color="white" boxShadow="md" fontWeight="700">{to}</Box>
            </Flex>
          ))}
        </Box>
      )
    },
    {
      number: "03",
      title: "Start Syncing Instantly",
      description: "Everything stays in sync. Updates to status, descriptions, and relationships happen in real-time across all your tools.",
      visual: (
        <Flex alignItems="center" justifyContent="center" p={4} direction="column" gap={4}>
          <Box position="relative">
            <Flex
              w="80px" h="80px"
              borderRadius="full"
              bg="brand.primary"
              alignItems="center" justifyContent="center"
              boxShadow="0 12px 32px rgba(27,79,204,0.4)"
              position="relative"
              zIndex={1}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              >
                <Repeat size={32} color="white" strokeWidth={2.5} />
              </motion.div>
            </Flex>
            <Box 
               position="absolute" inset="-10px" 
               borderRadius="full" border="2px dashed" 
               borderColor="brand.300"
               style={{ animation: 'spin-slow 10s linear infinite' }}
            />
          </Box>
          <Flex alignItems="center" gap={2} bg="white" px={4} py={1.5} borderRadius="full" boxShadow="sm" borderWidth="1px" borderColor="teal.100">
            <Box w={2.5} h={2.5} borderRadius="full" bg="teal.500" />
            <Text fontSize="10px" fontWeight="800" color="success.800" letterSpacing="0.05em">SYSTEMS OPERATIONAL</Text>
          </Flex>
        </Flex>
      )
    }
  ];

  return (
    <Box
      as="section"
      id="how-it-works"
      bg="landing.surface"
      py={{ base: 24, md: 32 }}
      fontFamily="body"
      position="relative"
      overflow="hidden"
    >
      {/* Background Decor */}
      <Box 
        position="absolute" 
        top="0" left="0" right="0" bottom="0" 
        bg="radial-gradient(circle at 50% 50%, rgba(37, 99, 235, 0.05) 0%, transparent 60%)" 
        pointerEvents="none" 
      />
      <Box 
        position="absolute" 
        top="-10%" right="-5%" 
        w="40%" h="40%" 
        bg="radial-gradient(circle, rgba(34, 211, 238, 0.04) 0%, transparent 70%)" 
        pointerEvents="none" 
      />
      
      <Box maxW="1100px" mx="auto" px={{ base: 5, md: 8 }} position="relative" zIndex={1}>
        {/* Header */}
        <Box textAlign="center" mb={20}>
          <Flex
            display="inline-flex"
            alignItems="center"
            px={3} py={1.5}
            borderRadius="full"
            bg="brand.subtle"
            borderWidth="1px"
            borderColor="landing.border"
            mb={4}
            mx="auto"
          >
            <Text fontSize="xs" fontWeight="800" color="brand.primary" letterSpacing="0.05em">THE BLUEPRINT</Text>
          </Flex>
          <Text
            as="h2"
            fontSize={{ base: '2xl', md: '4xl' }}
            fontWeight="900"
            color="text.main"
            letterSpacing="-0.02em"
            mb={4}
          >
            Go Live in{' '}
            <Box as="span" color="brand.primary">Under 5 Minutes</Box>
          </Text>
          <Text fontSize="lg" color="text.muted" maxW="520px" mx="auto" lineHeight="1.7">
            No complex configurations. No scripts. No manual mapping hell.{' '}
            <Box as="span" fontWeight="700" color="text.main">One 60-second connection to unify your workflow.</Box>
          </Text>
        </Box>

        {/* Steps */}
        <Box position="relative">
          {/* Vertical line */}
          <Box
            position="absolute"
            left="50%"
            top={0}
            bottom={0}
            w="2px"
            bg="brand.300"
            transform="translateX(-50%)"
            display={{ base: 'none', md: 'block' }}
          >
            <motion.div
              style={{ width: '100%', background: 'linear-gradient(to bottom, brand.primary, #3B72E8)', height: '0%' }}
              whileInView={{ height: '100%' }}
              transition={{ duration: 1.5, ease: "easeOut" }}
            />
          </Box>

          <Flex direction="column" gap={16}>
            {steps.map((step, index) => (
              <Flex
                key={index}
                alignItems="center"
                direction={{ base: 'column', md: index % 2 === 0 ? 'row' : 'row-reverse' }}
                gap={{ base: 10, md: 16 }}
              >
                {/* Text side */}
                <Box flex={1}>
                  <Flex align="center" gap={3} mb={4}>
                    <Flex 
                      w="32px" h="32px" 
                      borderRadius="md" 
                      bg="brand.subtle" 
                      color="brand.primary" 
                      alignItems="center" 
                      justifyContent="center"
                      fontWeight="800"
                      fontSize="sm"
                    >
                      {step.number}
                    </Flex>
                    <Text fontSize="xs" fontWeight="800" color="brand.primary" letterSpacing="0.1em">
                      PHASE
                    </Text>
                  </Flex>
                  <Text
                    as="h3"
                    fontSize="2xl"
                    fontWeight="800"
                    color="text.main"
                    mb={4}
                    letterSpacing="-0.01em"
                  >
                    {step.title}
                  </Text>
                  <Text fontSize="md" color="text.secondary" lineHeight="1.8">
                    {step.description}
                  </Text>
                </Box>

                {/* Center node (desktop) */}
                <Box
                  position="relative"
                  zIndex={2}
                  display={{ base: 'none', md: 'flex' }}
                  w="32px"
                  h="32px"
                  flexShrink={0}
                  borderRadius="full"
                  bg="white"
                  borderWidth="3px"
                  borderColor="brand.primary"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="md"
                >
                   <Box w="8px" h="8px" borderRadius="full" bg="brand.primary" />
                </Box>

                {/* Visual side */}
                <Box
                  flex={1}
                  w="100%"
                  p={8}
                  borderRadius="2xl"
                  bg="transparent"
                  borderWidth="1px"
                  borderColor="landing.border"
                  minH="260px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="sm"
                >
                  {step.visual}
                </Box>
              </Flex>
            ))}
          </Flex>
        </Box>

        {/* CTA */}
        <Box textAlign="center" mt={24}>
          <Box
            as="button"
            px={10} py={4}
            borderRadius="xl"
            bg="brand.primary"
            color="white"
            fontWeight="800"
            fontSize="md"
            border="none"
            cursor="pointer"
            transition="all 200ms"
            _hover={{ transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(27,79,204,0.3)', bg: 'brand.700' }}
            style={{ fontFamily: 'inherit' }}
            onClick={() => navigate('/signup')}
          >
            Build Your Bridge Now
          </Box>
          <Text fontSize="sm" color="text.muted" mt={4} fontWeight="500">No credit card required. Connect in 60 seconds.</Text>
        </Box>
      </Box>
      
      <style>{`
        @keyframes ping {
          75%, 100% {
            transform: scale(1.5);
            opacity: 0;
          }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default HowItWorks;
